import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/fr';
import pb from '../../../lib/pocketbase';
import { createPortal } from 'react-dom';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../Admin.css';

moment.locale('fr', {
    week: { dow: 1, doy: 4 },
});
const localizer = momentLocalizer(moment);

const Planning = () => {
    const location = useLocation();
    const [appointments, setAppointments] = useState([]);
    const [pendingQuotes, setPendingQuotes] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(Views.MONTH);
    const [date, setDate] = useState(new Date());

    const [showManualModal, setShowManualModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [manualData, setManualData] = useState({
        firstName: '', lastName: '', email: '', phone: '',
        date: '', time: '09:00', service: 'Reproduction de clés', vehicle_details: '', notes: ''
    });

    const fetchAllData = async () => {
        try {
            // 1. Fetch Appointments
            const records = await pb.collection('appointments').getFullList({ sort: '-created' });
            const formatted = records.map(app => ({
                id: app.id,
                title: `${app.firstName} ${app.lastName}`,
                start: moment(`${app.date} ${app.time}`, 'YYYY-MM-DD HH:mm').toDate(),
                end: moment(`${app.date} ${app.time}`, 'YYYY-MM-DD HH:mm').add(1, 'hour').toDate(),
                resource: app,
                status: app.status
            }));
            setAppointments(formatted);

            // 2. Fetch Pending Quotes (Prop 1)
            const quotes = await pb.collection('quotes').getFullList({
                filter: 'status = "valide"',
                sort: '-created'
            });
            setPendingQuotes(quotes);
        } catch (err) {
            console.error('Erreur fetch:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let unsubscribeApps;
        let unsubscribeQuotes;

        const setup = async () => {
            await fetchAllData();
            unsubscribeApps = await pb.collection('appointments').subscribe('*', () => fetchAllData());
            unsubscribeQuotes = await pb.collection('quotes').subscribe('*', () => fetchAllData());
        };
        setup();
        return () => {
            if (unsubscribeApps) unsubscribeApps();
            if (unsubscribeQuotes) unsubscribeQuotes();
        };
    }, []);

    // Handle deep link from Requests (Prop 2)
    useEffect(() => {
        if (location.state?.planQuote) {
            const q = location.state.planQuote;
            setManualData({
                firstName: q.firstName, lastName: q.lastName, email: q.email, phone: q.phone,
                service: q.services_list?.split(',')[0] || 'Reproduction de clés',
                vehicle_details: `${q.vehicle_brand} ${q.vehicle_model} (${q.vehicle_plate})`,
                notes: `Devis #${q.id.slice(-4).toUpperCase()}\nNotes: ${q.notes || 'Aucune'}`,
                date: '', time: '09:00'
            });
            setShowManualModal(true);
            // Clear state to avoid reopening on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            await pb.collection('appointments').update(id, { status: newStatus });
        } catch (err) {
            alert('Erreur: ' + err.message);
        }
    };

    const handleAddManual = async () => {
        try {
            await pb.collection('appointments').create({ ...manualData, status: 'confirmed' });
            setShowManualModal(false);
        } catch (err) {
            alert('Erreur: ' + err.message);
        }
    };

    const CustomToolbar = (toolbar) => {
        return (
            <div className="custom-calendar-header">
                <div className="header-left">
                    <div className="nav-buttons">
                        <button onClick={() => toolbar.onNavigate('PREV')} className="nav-btn">←</button>
                        <button onClick={() => toolbar.onNavigate('TODAY')} className="today-btn">Aujourd'hui</button>
                        <button onClick={() => toolbar.onNavigate('NEXT')} className="nav-btn">→</button>
                    </div>
                    <div className="calendar-title-wrapper">
                        <span className="calendar-month">{moment(toolbar.date).format('MMMM')}</span>
                        <span className="calendar-year">{moment(toolbar.date).format('YYYY')}</span>
                    </div>
                </div>
                <div className="header-right">
                    <div className="view-switcher">
                        <button onClick={() => setView(Views.MONTH)} className={view === Views.MONTH ? 'active' : ''}>Mois</button>
                        <button onClick={() => setView(Views.WEEK)} className={view === Views.WEEK ? 'active' : ''}>Semaine</button>
                        <button onClick={() => setView(Views.DAY)} className={view === Views.DAY ? 'active' : ''}>Jour</button>
                    </div>
                    <div className="stats-badge">
                        <span className="count">{appointments.length}</span>
                        <span className="label">Interventions</span>
                    </div>
                </div>
            </div>
        );
    };

    const CustomEvent = ({ event }) => {
        const getIcon = (s) => (s?.includes('clé') ? '🔑' : s?.includes('Éth') ? '🧪' : '💻');
        return (
            <div className="custom-event-content">
                <span>{getIcon(event.resource.service)}</span>
                <span className="event-time">{moment(event.start).format('HH:mm')}</span>
                <span className="event-title-text">{event.title}</span>
            </div>
        );
    };

    if (loading && appointments.length === 0) return <div className="admin-content-wrapper">Chargement...</div>;

    return (
        <div className="admin-content-wrapper planning-dashboard">
            <header className="intro-card admin-intro">
                <div className="admin-header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div>
                        <h2>Centre d'Opérations</h2>
                        <p>Synchronisation en direct avec les devis validés.</p>
                    </div>
                    <div className="admin-actions">
                        <button className="primary-button" onClick={() => {
                            setManualData({ firstName: '', lastName: '', email: '', phone: '', date: '', time: '09:00', service: 'Reproduction de clés', vehicle_details: '', notes: '' });
                            setShowManualModal(true);
                        }}>+ Nouveau RDV</button>
                    </div>
                </div>
            </header>

            <div className="planning-layout-grid">
                <aside className="pending-quotes-sidebar">
                    <div className="sidebar-header">
                        <h3>Files d'attente</h3>
                        <span className="pending-count">{pendingQuotes.length}</span>
                    </div>
                    <div className="quotes-list-scroll">
                        {pendingQuotes.length === 0 ? (
                            <div className="empty-sidebar">Tout est planifié ! ✨</div>
                        ) : (
                            pendingQuotes.map(q => (
                                <div key={q.id} className="pending-quote-card" onClick={() => {
                                    setManualData({
                                        firstName: q.firstName, lastName: q.lastName, email: q.email, phone: q.phone,
                                        service: q.services?.split(',')[0] || 'Reproduction de clés',
                                        vehicle_details: `${q.brand} ${q.model} (${q.licensePlate})`,
                                        notes: `Devis #${q.id.slice(-4).toUpperCase()}\nNotes: ${q.notes || 'Aucune'}`,
                                        date: '', time: '09:00'
                                    });
                                    setShowManualModal(true);
                                }}>
                                    <div className="quote-badge-row">
                                        <span className="quote-type-tag">{q.services?.split(',')[0]}</span>
                                        <span className="quote-price">{q.totalPriceTTC}€</span>
                                    </div>
                                    <div className="quote-client-name">{q.firstName} {q.lastName}</div>
                                    <button className="quick-plan-btn">Planifier →</button>
                                </div>
                            ))
                        )}
                    </div>
                </aside>

                <main className="calendar-section">
                    <Calendar
                        localizer={localizer}
                        events={appointments}
                        view={view}
                        onView={setView}
                        date={date}
                        onNavigate={setDate}
                        style={{ height: '780px' }}
                        components={{ toolbar: CustomToolbar, event: CustomEvent }}
                        eventPropGetter={(e) => ({
                            style: {
                                background: e.status === 'pending' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 
                                           e.status === 'completed' ? 'linear-gradient(135deg, #10b981, #059669)' :
                                           'linear-gradient(135deg, #3b82f6, #2563eb)',
                                borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                        })}
                        onSelectEvent={e => setSelectedEvent(e.resource)}
                    />
                </main>
            </div>

            {/* SIDE DRAWER */}
            {selectedEvent && createPortal(
                <div className="drawer-overlay" onClick={() => setSelectedEvent(null)}>
                    <div className="side-drawer" onClick={e => e.stopPropagation()}>
                        <div className="drawer-header">
                            <span className={`status-pill ${selectedEvent.status}`}>
                                {selectedEvent.status === 'pending' ? '🟡 En attente' : 
                                 selectedEvent.status === 'confirmed' ? '🔵 Confirmé' : 
                                 selectedEvent.status === 'completed' ? '🟢 Terminé' : selectedEvent.status}
                            </span>
                            <button className="close-drawer" onClick={() => setSelectedEvent(null)}>×</button>
                        </div>

                        <div className="drawer-body">
                            <div className="client-hero">
                                <div className="client-avatar">
                                    {selectedEvent.firstName?.charAt(0) || 'U'}
                                </div>
                                <div className="client-meta">
                                    <h3>{selectedEvent.firstName} {selectedEvent.lastName}</h3>
                                    <a href={`tel:${selectedEvent.phone}`} className="phone-link">
                                        {selectedEvent.phone}
                                    </a>
                                </div>
                            </div>

                            <div className="drawer-details-grid">
                                <div className="detail-item full-width">
                                    <span className="label">Prestation</span>
                                    <div className="value-prominent">
                                        🔧 {selectedEvent.service}
                                    </div>
                                </div>

                                <div className="detail-item">
                                    <span className="label">Date & Heure</span>
                                    <div className="value">
                                        📅 {selectedEvent.date} à {selectedEvent.time}
                                    </div>
                                </div>

                                <div className="detail-item">
                                    <span className="label">Véhicule</span>
                                    <div className="value">
                                        🚗 {(!selectedEvent.vehicle_details || selectedEvent.vehicle_details.includes('undefined')) 
                                            ? 'Véhicule non renseigné' 
                                            : selectedEvent.vehicle_details}
                                    </div>
                                </div>
                            </div>

                            {selectedEvent.notes && (
                                <div className="drawer-notes">
                                    <span className="label">Notes</span>
                                    <div className="notes-box">
                                        {selectedEvent.notes}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="drawer-footer">
                            <div className="footer-actions-stack">
                                {selectedEvent.status === 'pending' && (
                                    <button 
                                        className="action-btn confirm-btn"
                                        onClick={() => { handleStatusUpdate(selectedEvent.id, 'confirmed'); setSelectedEvent(null); }}
                                    >
                                        Confirmer le RDV
                                    </button>
                                )}
                                
                                {selectedEvent.status === 'confirmed' && (
                                    <button 
                                        className="action-btn complete-btn"
                                        onClick={() => { handleStatusUpdate(selectedEvent.id, 'completed'); setSelectedEvent(null); }}
                                    >
                                        Terminer l'intervention
                                    </button>
                                )}

                                <button className="action-btn close-btn" onClick={() => setSelectedEvent(null)}>
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>, document.body
            )}

            {/* MODAL */}
            {showManualModal && createPortal(
                <div className="modal-backdrop" onClick={() => setShowManualModal(false)}>
                    <div className="modal-content admin-modal" onClick={e => e.stopPropagation()}>
                        <h3>Planification d'Intervention</h3>
                        <div className="pricing-input-grid" style={{ marginTop: '1.5rem' }}>
                            <div className="form-group"><label>Prénom</label><input className="admin-input" value={manualData.firstName} onChange={e => setManualData({...manualData, firstName: e.target.value})} /></div>
                            <div className="form-group"><label>Nom</label><input className="admin-input" value={manualData.lastName} onChange={e => setManualData({...manualData, lastName: e.target.value})} /></div>
                            <div className="form-group"><label>Date</label><input type="date" className="admin-input" value={manualData.date} onChange={e => setManualData({...manualData, date: e.target.value})} /></div>
                            <div className="form-group"><label>Heure</label><input type="time" className="admin-input" value={manualData.time} onChange={e => setManualData({...manualData, time: e.target.value})} /></div>
                            <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                <label>Service</label>
                                <select className="admin-input" value={manualData.service} onChange={e => setManualData({...manualData, service: e.target.value})}>
                                    <option>Reproduction de clés</option><option>Conversion Éthanol</option><option>Diagnostic Electronique</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Véhicule</label><input className="admin-input" value={manualData.vehicle_details} onChange={e => setManualData({...manualData, vehicle_details: e.target.value})} /></div>
                        </div>
                        <div className="admin-modal-footer" style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button className="secondary-button" onClick={() => setShowManualModal(false)}>Annuler</button>
                            <button className="primary-button" onClick={handleAddManual}>Enregistrer le RDV</button>
                        </div>
                    </div>
                </div>, document.body
            )}
        </div>
    );
};

export default Planning;
